import React, { useState } from 'react';
import { Search, Globe2, MapPin, Layers, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { REGIONS, SUB_REGIONS } from '../mock/locations';
import { SERVICES_KEYWORDS, LANG_LABELS } from '../mock/serviceKeywords';

const ServiceAreas = () => {
  const [activeLang, setActiveLang] = useState('en');
  const allCities = REGIONS.flatMap(r => r.countries.flatMap(c => c.cities.map(city => ({ city, country: c.name, region: r.name }))));
  const langCodes = Object.keys(LANG_LABELS);

  return (
    <section className="py-20 bg-white" aria-labelledby="service-areas-heading">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-green-600 font-bold text-[12px] uppercase tracking-[0.25em] mb-3">Service Coverage</div>
          <h2 id="service-areas-heading" className="text-4xl md:text-5xl font-black text-[#0e1116]">Engineering Services <span className="text-orange-500">Where You Are</span></h2>
          <p className="text-gray-500 mt-4">Land Drafters delivers CAD drafting, BIM, GIS, structural, MEP and infrastructure engineering services across major metropolitan and regional markets worldwide.</p>
        </div>

        {/* Sub-regions */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {Object.entries(SUB_REGIONS).map(([region, countries]) => (
            <div key={region} className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-lg p-5 hover:border-green-400 hover:shadow-md transition-all">
              <div className="flex items-center gap-2 mb-3">
                <Globe2 className="w-4 h-4 text-green-600" />
                <h3 className="font-bold text-[#0e1116]">{region}</h3>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {countries.map(c => (
                  <span key={c} className="text-[11px] bg-gray-50 text-green-700 border border-gray-200 px-2 py-0.5 rounded">{c}</span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Multilingual keywords block (all 20 services, all 20 languages) */}
        <div className="mt-14 bg-[#0e1116] rounded-lg p-8 md:p-10 text-white">
          <div className="flex items-start justify-between flex-wrap gap-4 mb-5">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Search className="w-5 h-5 text-orange-400" />
                <h3 className="text-2xl font-black">Services in <span className="text-green-400">Every Language</span></h3>
              </div>
              <p className="text-gray-400 text-sm max-w-2xl">All {SERVICES_KEYWORDS.length} main services with sub-services translated across {langCodes.length} languages — for clients, search engines, and local market discovery.</p>
            </div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-gray-500">
              <Layers className="w-3.5 h-3.5 text-orange-400" />
              {SERVICES_KEYWORDS.length} services × {langCodes.length} languages = {SERVICES_KEYWORDS.length * langCodes.length} keyword variants
            </div>
          </div>

          {/* Language tabs */}
          <div className="flex flex-wrap gap-1.5 mb-6 pb-4 border-b border-gray-900/40">
            {langCodes.map(lc => (
              <button key={lc} onClick={() => setActiveLang(lc)} className={`px-3 py-1.5 rounded text-[11px] font-bold uppercase tracking-wider transition-colors ${activeLang === lc ? 'bg-orange-500 text-white' : 'bg-[#1a1f2e] border border-gray-900/40 text-gray-400 hover:text-green-400 hover:border-green-700'}`}>
                {lc} <span className="opacity-60 normal-case">{LANG_LABELS[lc]}</span>
              </button>
            ))}
          </div>

          {/* Services grid for selected language */}
          <div className="grid md:grid-cols-2 gap-3">
            {SERVICES_KEYWORDS.map(s => (
              <details key={s.slug} className="bg-[#1a1f2e] border border-gray-900/40 rounded p-4 group">
                <summary className="flex items-center justify-between cursor-pointer">
                  <div>
                    <div className="font-bold text-sm uppercase tracking-wider text-green-400">{s.name}</div>
                    <div className="text-[12px] text-orange-300 mt-1" dir={activeLang === 'ar' ? 'rtl' : 'ltr'}>{s.t[activeLang] || s.t.en}</div>
                  </div>
                  <span className="text-orange-400 text-xs group-open:rotate-45 transition-transform shrink-0 ml-2">+</span>
                </summary>
                <div className="mt-3 pt-3 border-t border-gray-900/40">
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">Sub-services</div>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {s.subServices.map(sub => (
                      <span key={sub} className="text-[11px] bg-gray-900/30 border border-gray-700/40 text-green-100 px-2 py-1 rounded">{sub}</span>
                    ))}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">All language variants</div>
                  <div className="flex flex-wrap gap-1.5">
                    {langCodes.map(lc => (
                      <span key={lc} className="text-[11px] bg-orange-500/10 border border-orange-700/40 text-orange-200 px-2 py-1 rounded" dir={lc === 'ar' ? 'rtl' : 'ltr'} title={LANG_LABELS[lc]}>
                        <span className="text-green-400 font-bold mr-1">{lc.toUpperCase()}</span>{s.t[lc] || s.t.en}
                      </span>
                    ))}
                  </div>
                  <Link to={`/services/${s.slug}`} className="inline-flex items-center gap-1 text-[11px] text-green-400 hover:text-orange-400 font-bold mt-3 uppercase tracking-wider">
                    Explore service <ChevronRight className="w-3 h-3" />
                  </Link>
                </div>
              </details>
            ))}
          </div>
        </div>

        {/* Cities served */}
        <div className="mt-14">
          <div className="flex items-center gap-3 mb-5">
            <MapPin className="w-5 h-5 text-green-600" />
            <h3 className="text-2xl font-black text-[#0e1116]">Cities <span className="text-orange-500">We Serve</span></h3>
          </div>
          <p className="text-gray-500 text-sm mb-6 max-w-3xl">From major metropolitan centres to regional and rural hubs, our distributed delivery model covers project sites worldwide.</p>
          <div className="flex flex-wrap gap-1.5">
            {allCities.map((entry) => (
              <span key={`${entry.city}-${entry.country}`} title={`${entry.country} • ${entry.region}`} className="text-[11px] bg-gray-100 border border-gray-200 text-gray-700 px-2.5 py-1 rounded hover:bg-gray-50 hover:border-green-300 hover:text-green-700 transition-colors cursor-default">
                {entry.city}
              </span>
            ))}
          </div>
        </div>

        {/* Hidden but indexed SEO content - all keywords aggregated */}
        <div className="sr-only" aria-hidden="false">
          <h3>Multilingual Engineering Service Keywords</h3>
          {SERVICES_KEYWORDS.map(s => (
            <p key={s.slug}>
              <strong>{s.name}:</strong> {Object.values(s.t).join(' · ')}. Sub-services: {s.subServices.join(', ')}.
            </p>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServiceAreas;
